// src/components/steps/SkillsExpertise.jsx
import React, { useState, useEffect } from "react";
import SkillsIcon from "../../assets/ProfileSetup/SkillAndExpertise.svg";
import TagInput from "./AccountSetup/TagInput";
import SkipToDashboardButton from "../../components/dashboard/common/SkipToDashboardButton";

// --- Reusable Saving Animation Component ---
const SavingAnimation = ({ isSaving, message, subMessage }) => {
  if (!isSaving) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-white bg-opacity-80 backdrop-blur-sm flex flex-col items-center justify-center z-50 transition-opacity duration-300"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="text-center p-8 rounded-lg">
        <svg
          className="animate-spin h-10 w-10 text-emerald-600 mb-4 mx-auto"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p className="text-lg font-semibold text-gray-800">
          {message || "Saving..."}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          {subMessage || "Please wait a moment."}
        </p>
      </div>
    </div>
  );
};

function SkillsExpertise({
  data,
  onNext,
  onBack,
  isSubmitting,
  onSkipToDashboard,
}) {
  const [formState, setFormState] = useState({
    skills: [],
    licenses: [],
    ...(data || {}),
  });
  const [error, setError] = useState(null);
  const [isFinalizing, setIsFinalizing] = useState(false); // <-- NEW local state for delay

  // --- NEW combined loading state ---
  const isLoading = isSubmitting || isFinalizing;

  useEffect(() => {
    if (data && typeof data === "object") {
      // Combine existing skills and tools into a single skills array
      const existingSkills = Array.isArray(data.skills) ? data.skills : [];
      const existingTools = Array.isArray(data.tools) ? data.tools : [];
      const combinedSkills = [
        ...new Set([...existingSkills, ...existingTools]),
      ];

      setFormState((prev) => ({
        ...prev,
        skills: combinedSkills,
        licenses: Array.isArray(data.licenses)
          ? data.licenses
          : prev.licenses || [],
      }));
    }
  }, [data]);

  const handleAddSkill = (skill) => {
    if (skill && typeof skill === "string" && skill.trim()) {
      setFormState((prev) => ({
        ...prev,
        skills: [...new Set([...(prev.skills || []), skill.trim()])],
      }));
    }
  };
  const handleRemoveSkill = (index) => {
    setFormState((prev) => ({
      ...prev,
      skills: (prev.skills || []).filter((_, i) => i !== index),
    }));
  };
  const handleAddLicense = (license) => {
    if (license && typeof license === "string" && license.trim()) {
      setFormState((prev) => ({
        ...prev,
        licenses: [...new Set([...(prev.licenses || []), license.trim()])],
      }));
    }
  };
  const handleRemoveLicense = (index) => {
    setFormState((prev) => ({
      ...prev,
      licenses: (prev.licenses || []).filter((_, i) => i !== index),
    }));
  };

  // --- UPDATED to include delay ---
  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    if (!formState.skills || formState.skills.length === 0) {
      setError("Please add at least one skill to complete your profile.");
      return;
    }

    setIsFinalizing(true);
    setTimeout(() => {
      // Send data in the format expected by the parent component
      const dataToSend = {
        skills: formState.skills,
        tools: [], // Empty array since we're combining everything into skills
        licenses: formState.licenses,
      };
      onNext(dataToSend);
    }, 1000); // 1-second delay for UX
  };

  const primaryButtonStyle =
    "inline-flex items-center justify-center rounded-md border border-transparent bg-emerald-700 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:opacity-50";
  const secondaryButtonStyle =
    "rounded-md border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50";

  return (
    <div className="skills-expertise p-6 md:p-8">
      <SavingAnimation
        isSaving={isLoading}
        message="Completing Registration..."
        subMessage="Saving..."
      />
      <div className="form-header mb-6 text-center">
        <div className="flex justify-center mb-4">
          <img src={SkillsIcon} alt="Skills" className="w-16 h-16" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Skills & Expertise</h2>
        <p className="text-gray-600 mt-1">
          This is the final step! List your skills to complete your profile.
        </p>
      </div>
      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 border border-red-200">
          <div className="flex">
            <div className="ml-2">
              <h3 className="text-sm font-medium text-red-800">Input Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="ml-auto pl-3">
              <span className="text-red-500 hover:text-red-700">×</span>
            </button>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="form-group mb-6">
          <TagInput
            label="Skills *"
            tags={formState.skills || []}
            onAddTag={handleAddSkill}
            onRemoveTag={handleRemoveSkill}
            placeholder="E.g., Python, Leadership, Project Management, Figma, Photoshop (Press Enter)"
            disabled={isLoading}
          />
          <p className="mt-1 text-xs text-gray-500">
            List your technical skills, soft skills, and tools/software
            proficiency. (Required)
          </p>
        </div>
        <div className="form-group mb-6">
          <TagInput
            label="Licenses & Certifications"
            tags={formState.licenses || []}
            onAddTag={handleAddLicense}
            onRemoveTag={handleRemoveLicense}
            placeholder="E.g., PMP, CISSP, AWS Certified Developer (Press Enter)"
            disabled={isLoading}
          />
          <p className="mt-1 text-xs text-gray-500">
            Relevant professional licenses or certifications.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center pt-8 gap-4 border-t border-gray-200 mt-10">
          <div className="flex flex-col sm:flex-row gap-x-4 gap-y-4 w-full sm:w-auto order-last sm:order-first">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                disabled={isLoading}
                className={`${secondaryButtonStyle} w-full sm:w-auto`}
              >
                Back
              </button>
            )}

            {onSkipToDashboard && (
              <SkipToDashboardButton
                onSkip={onSkipToDashboard}
                disabled={isLoading}
                className="w-full sm:w-auto"
              />
            )}
          </div>

          <button
            type="submit"
            disabled={
              isLoading || !formState.skills || formState.skills.length === 0
            }
            className={`${primaryButtonStyle} w-full sm:w-auto order-first sm:order-last`}
          >
            {isSubmitting || isFinalizing
              ? "Completing..."
              : "Complete Registration"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SkillsExpertise;
